import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { ArrowLeft, Building2, Mail, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AltchaWidget from "../components/AltchaWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [altchaPayload, setAltchaPayload] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [senderEmail, setSenderEmail] = useState("");

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/auth/config/sender-email`)
      .then((resp) => {
        if (mounted && resp.data?.sender_email) {
          setSenderEmail(resp.data.sender_email);
        }
      })
      .catch(() => {
        /* fallback silencioso: el aviso al usuario igual es genérico */
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!altchaPayload) {
      toast.error("Por favor complete la verificación anti-bot ALTCHA");
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        altcha_payload: altchaPayload,
      });
      setSubmitted(true);
      toast.success(resp.data?.message || "Solicitud recibida");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No se pudo procesar la solicitud. Intente nuevamente.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de confirmacion (anti-enumeration: misma respuesta exista o no el email)
  if (submitted) {
    return (
      <div className="min-h-screen flex login-pattern">
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white">
          <div className="max-w-md text-center">
            <div className="flex items-center justify-center mb-8">
              <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <ShieldCheck className="w-10 h-10 text-yellow-400" />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recuperación de cuenta
            </h1>
            <p className="text-lg text-slate-300">
              Por su seguridad enviamos instrucciones únicamente cuando la cuenta existe y es del tipo Contribuyente. El enlace caduca en 15 minutos.
            </p>
          </div>
        </div>
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
          <Card className="w-full max-w-md shadow-xl border-0 animate-fade-in" data-testid="forgot-confirmation-card">
            <CardHeader className="text-center pb-2">
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Revise su correo
              </CardTitle>
              <CardDescription className="text-slate-500">
                Si la cuenta existe y es de tipo contribuyente, le hemos enviado un enlace seguro a su correo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 space-y-2">
                <p data-testid="forgot-sender-email-info">
                  📧 El correo proviene de{" "}
                  <strong data-testid="forgot-sender-email-value">
                    {senderEmail || "su remitente SIRET configurado"}
                  </strong>
                  .
                </p>
                <p>⏱ El enlace vence en <strong>15 minutos</strong> y solo puede usarse una vez.</p>
                <p>🔒 No comparta este enlace con nadie. SIRET nunca le pedirá su contraseña.</p>
              </div>
              <p className="text-xs text-slate-500 text-center">
                ¿No recibió el correo? Revise su bandeja de spam o solicite uno nuevo después de unos minutos.
              </p>
              <Button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full bg-blue-800 hover:bg-blue-900 text-white"
                data-testid="forgot-back-to-login"
              >
                Volver al inicio de sesión
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex login-pattern">
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center mb-8">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
              <Building2 className="w-10 h-10 text-yellow-400" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recuperar contraseña
          </h1>
          <p className="text-lg text-slate-300 mb-6">
            Le enviaremos un enlace seguro a su correo registrado para que pueda crear una nueva contraseña.
          </p>
          <ul className="text-sm text-slate-300 space-y-2 text-left">
            <li>• Disponible solo para cuentas de <strong>Contribuyente</strong>.</li>
            <li>• El enlace es <strong>de un solo uso</strong> y expira en 15 minutos.</li>
            <li>• Las sesiones activas se cierran automáticamente al cambiar la clave.</li>
            <li>• Si no fue usted, ignore el correo: su contraseña actual seguirá vigente.</li>
          </ul>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md shadow-xl border-0 animate-fade-in" data-testid="forgot-password-card">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-between mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                className="text-slate-500 hover:text-slate-700"
                data-testid="forgot-btn-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Volver
              </Button>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Contribuyente
              </span>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              ¿Olvidó su contraseña?
            </CardTitle>
            <CardDescription className="text-slate-500">
              Ingrese el correo asociado a su cuenta y le enviaremos un enlace para restablecerla.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-slate-700">Correo Electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="forgot-email"
                    data-testid="forgot-email-input"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <AltchaWidget onVerified={setAltchaPayload} testid="altcha-forgot-password" />

              <Button
                type="submit"
                className="w-full text-white bg-green-600 hover:bg-green-700"
                data-testid="forgot-submit"
                disabled={loading || !altchaPayload}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Enviando...
                  </span>
                ) : "Enviar enlace de restablecimiento"}
              </Button>

              <p className="text-center text-xs text-slate-500 pt-3 border-t border-slate-100">
                ¿Recordó su clave?{" "}
                <Link to="/login" className="text-blue-700 font-semibold underline" data-testid="forgot-link-login">
                  Iniciar sesión
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
